import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DetailedPageLayout from '../components/DetailedPageLayout';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomBarChart from '../../../components/Charts/CustomBarChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';
import StatCard from '../components/ui/StatCard';
import SectionCard from '../components/ui/SectionCard';
import DataTable from '../components/ui/DataTable';
import dashboardAPI from '../../../services/dashboardService';
import { Users, UserPlus, UserCheck, TrendingUp } from 'lucide-react';

const RECENT_USER_COLUMNS = [
    { key: 'name', header: 'Name' },
    { key: 'type', header: 'Type' },
    {
        key: 'created_at',
        header: 'Registered',
        width: '140px',
        render: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A')
    }
];

const DetailedUsersPage = ({ isSubView = false, filters: externalFilters, onFilterChange: externalOnFilterChange }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState({ timeRange: '30days', category: 'all' });

    const filters = externalFilters || localFilters;

    const fetchStats = useCallback(async (newFilters = filters) => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getUserStats(newFilters);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch user stats', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const onFilterChange = externalOnFilterChange || ((newFilters) => {
        setLocalFilters(newFilters);
        fetchStats(newFilters);
    });

    useEffect(() => {
        fetchStats(filters);
    }, [fetchStats, filters]);

    const userTypeData = useMemo(() => {
        if (!stats?.usersByType) return [];
        return Object.entries(stats.usersByType).map(([key, value]) => ({
            name: key.replace(/_/g, ' ').toLowerCase(),
            value
        }));
    }, [stats?.usersByType]);

    const graphData = stats?.graph_data || [];

    if (loading || !stats) {
        return <div className="sd-loading-message">Loading statistics...</div>;
    }

    return (
        <DetailedPageLayout
            title="User Analytics"
            onFilterChange={onFilterChange}
            filters={filters}
            isSubView={isSubView}
        >
            <div className="sd-grid sd-grid--kpi-users">
                <StatCard label="Total Users" value={stats.totalUsers} icon={Users} color="bg-blue-500" />
                <StatCard label="New Users" value={stats.newUsers} icon={UserPlus} color="bg-emerald-500" />
                <StatCard label="Active Users" value={stats.activeUsers} icon={UserCheck} color="bg-violet-500" />
            </div>

            <div className="sd-grid sd-grid--charts">
                <SectionCard title="User Distribution">
                    <CustomDonutChart
                        data={userTypeData}
                        totalValue={stats.totalUsers}
                        title=""
                    />
                </SectionCard>
                <SectionCard title="User Growth" className="sd-card--wide">
                    <CustomAreaChart data={graphData} color="#3b82f6" title="" />
                </SectionCard>
            </div>

            <div className="sd-grid sd-grid--charts sd-grid--last">
                <SectionCard title="Registration Dynamics" className="sd-card--wide">
                    <CustomBarChart data={graphData} title="" />
                </SectionCard>
                <SectionCard title="Recent Registrations" icon={TrendingUp}>
                    <DataTable
                        columns={RECENT_USER_COLUMNS}
                        data={stats.recentUsers || []}
                        emptyText="No recent registrations"
                    />
                </SectionCard>
            </div>
        </DetailedPageLayout>
    );
};

export default DetailedUsersPage;
